'use client';

import React, { useEffect, useState } from 'react';

interface CrashRecoveryUIProps {
  onRecover: () => void;
}

export default function CrashRecoveryUI({ onRecover }: CrashRecoveryUIProps) {
  const [visible, setVisible] = useState(false);
  const [countdown, setCountdown] = useState(3);

  useEffect(() => {
    // Brief delay before showing recovery UI (crash animation plays first)
    const showTimer = setTimeout(() => setVisible(true), 400);
    return () => clearTimeout(showTimer);
  }, []);

  useEffect(() => {
    if (!visible) return;
    if (countdown <= 0) {
      onRecover();
      return;
    }
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [visible, countdown, onRecover]);

  if (!visible) return null;

  return (
    <div className="absolute inset-0 z-30 flex items-center justify-center pointer-events-none crash-overlay">
      <div
        className="flex flex-col items-center gap-5 pointer-events-auto stripe-modal-enter"
        style={{
          background: 'rgba(13,13,15,0.88)',
          backdropFilter: 'blur(16px)',
          border: '1px solid rgba(255,77,109,0.35)',
          borderRadius: '24px',
          padding: '32px 40px',
          maxWidth: '280px',
          width: '90%',
        }}
      >
        {/* Wipeout icon */}
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center"
          style={{ background: 'rgba(255,77,109,0.12)', border: '2px solid rgba(255,77,109,0.4)' }}
        >
          <span className="text-3xl">💥</span>
        </div>

        <div className="text-center">
          <p
            className="text-xl font-bold leading-tight"
            style={{ color: '#ff4d6d', fontFamily: 'DM Sans, sans-serif', textShadow: '0 0 12px rgba(255,77,109,0.6)' }}
          >
            BAILED!
          </p>
          <p
            className="text-sm mt-1"
            style={{ color: '#8899aa', fontFamily: 'DM Sans, sans-serif' }}
          >
            Combo reset. Get up and skate.
          </p>
        </div>

        {/* Auto-recovery countdown */}
        <div className="flex flex-col items-center gap-2 w-full">
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center"
            style={{ border: '2px solid rgba(255,77,109,0.4)' }}
          >
            <span className="font-mono-game text-2xl font-bold" style={{ color: '#ff4d6d' }}>
              {countdown}
            </span>
          </div>
          <p className="text-[11px] tracking-widest uppercase" style={{ color: '#556677', fontFamily: 'DM Sans, sans-serif' }}>
            Auto-recover
          </p>
        </div>

        {/* Manual recover button */}
        <button
          className="w-full py-3.5 rounded-xl font-bold text-base tracking-wide btn-action"
          style={{
            background: 'linear-gradient(135deg, #ff4d6d 0%, #c9184a 100%)',
            color: '#fff',
            fontFamily: 'DM Sans, sans-serif',
          }}
          onClick={onRecover}
          onTouchStart={e => { e.preventDefault(); onRecover(); }}
        >
          Get Up Now
        </button>
      </div>
    </div>
  );
}